import { Grid, Row, Col, Tabs, Tab } from "react-bootstrap";
import FailedToLoadAlert from "../FailedToLoadAlert";
import FeaturedProductTab from "../FeaturedProductTab";
import ImageSetUploader from "../ImageSetUploader";
import StoreModifierTab from "../StoreModifierTab";
import StoreProductTab from "../StoreProductTab";
import StoreProductModal from "../StoreProductModal";
import Loader from "../Loader";
import React, { PureComponent } from "react";
import { StyleSheet, css } from "aphrodite";
import * as stores from "../../lib/api/stores";
import * as productType from "../../enums/productType";
import * as menuStatus from "../../enums/menuStatus";
import PropTypes from "prop-types";
import logger from "../../lib/logger";
import StorePosSettingsTab from "../StorePosSettingsTab";
import ExternalTenderModal from "../ExternalTenderModal"
import DeleteConfirmModal from "../DeleteConfirmModal";
import AlertModal from "../AlertModal";

const styles = StyleSheet.create({
    alertFlag: {
        display: "inline-block",
        marginRight: 10,
    },
    tabWrapper: {
        marginTop: 20,
    },
});

const strings = {
    loading: "Loading",
    titleSuffix: "| Habit CMS",
    featured: "Featured",
    images: "Images",
    heroImage: "Hero Image",
    isolatedImage: "Isolated Image",
    modifiers: "Modifiers",
    edit: "Edit",
    store: "Store",
    items: "Items",
    combos: "Combos",
    posSettings: "POS Settings",
    confirmDelete: "Are you sure you want to delete this external tender?",
    nameCannotBeEmpty: "Name(s) cannot be empty. No changes were made.",
    namesMustBeUnique: "Tender names must be unique per store. No changes were made.",
    splitsMustBePositive: "Split percentages must be positive. No changes were made.",
    splitsMustAddUp: "Split percentage must add up to 100%. No changes were made.",
    splitsMustBeValid: "Split percentages must be valid numbers. No changes were made.",
    errorRefresh: "There was an error while refreshing this store's list of external tenders.",
    errorAdd: "There was an error when trying to add this external tender. No changes were made.",
    errorEdit: "There was an error when trying to edit this external tender. No changes were made.",
    errorDelete: "There was an error when trying to delete this external tender. No changes were made.",
    errorReorder: "There was an error when trying to reorder this external tender. No changes were made."
};

export default class StorePage extends PureComponent {
    static propTypes = {
        match: PropTypes.object.isRequired,
    };

    state = {
        store: null,
        initialLoading: true,
        showDeleteConfirmModal: false,
        showAlertModal: false,
        loading: false,
    };

    componentDidMount() {
        this._isMounted = true;
        document.title = `${strings.loading} ${strings.titleSuffix}`;
        stores
            .getStore(this.props.match.params.storeId)
            .then(resp => {
                this.setState({ store: resp });
                document.title = `${resp.name} ${strings.titleSuffix}`;
                return stores
                            .getStoreExternalTenders(this.props.match.params.storeId)
            })
            .then((resp) => {
                this.setState({
                    store: {
                        ...this.state.store,
                        tenders: resp ? resp.tenders : null
                    },
                    initialLoading: false
                })
            })
            .catch(err => {
                logger.warn(err);
                this.setState({ initialLoading: false });
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _showAlert = (alertMsg) => {
        this.setState({
            showAlertModal: true,
            alertMsg
        })
    }

    _handleUploadImage = (image, crop) => {
        this.setState({ loading: true })
        return stores.addImage(this.state.store.id, image, crop).then(images => {
            this.setState({
                store: {
                    ...this.state.store,
                    images,
                },
            });
            return images;
        })
        .finally(() => {
            this.setState({ loading: false })
        });
    };

    _handleSaveInfo = store => {
        this.setState({ store });
    };

    _onAddFeaturedProduct = featuredProductId => {
        this.setState({ loading: true })
        return stores
            .featureProduct(this.state.store.id, featuredProductId)
            .then(resp => {
                this.setState({
                    store: {
                        ...this.state.store,
                        featuredProducts: [...this.state.store.featuredProducts, resp],
                    },
                });
                return resp;
            })
            .catch(err => logger.warn(err))
            .finally(() => {
                this.setState({ loading: false })
            });
    };

    _onRemoveFeaturedProduct = featuredProductId => {
        this.setState({ loading: true })
        return stores.deleteFeaturedProduct(this.state.store.id, featuredProductId).then(resp => {
            this.setState({
                store: {
                    ...this.state.store,
                    featuredProducts: this.state.store.featuredProducts.filter(
                        fp => fp.id !== featuredProductId,
                    ),
                },
            });
            return resp;
        })
        .finally(() => {
            this.setState({ loading: false })
        });
    };

    _reorderFeaturedProduct = (featuredProduct, newIndex) => {
        this.setState({ loading: true })
        stores
            .reorderFeaturedProduct(this.state.store.id, featuredProduct.id, newIndex)
            .then(() => {
                const newFeatured = this.state.store.featuredProducts.filter(
                    f => f.id !== featuredProduct.id,
                );
                newFeatured.splice(newIndex, 0, featuredProduct);
                this.setState({
                    store: {
                        ...this.state.store,
                        featuredProducts: newFeatured,
                    },
                });
            })
            .catch(e => {
                logger.warn(e);
            })
            .finally(() => {
                this.setState({ loading: false })
            });
    };

    _onEditItem = i => {
        this.setState({
            currentProduct: i,
            currentProductType: productType.item,
        });
    };

    _onEditCombo = c => {
        this.setState({
            currentProduct: c,
            currentProductType: productType.combo,
        });
    };

    _onCancelEdit = () => {
        this.setState({
            currentProduct: null,
            currentTender: null,
        });
    };

    _onToggleModifierStatus = modifier => {
        const newStatus =
            modifier.status === menuStatus.available.value
                ? menuStatus.unavailable.value
                : menuStatus.available.value;
        this.setState({ loading: true })
        stores
            .updateModifier(this.state.store.id, modifier.modifierId, newStatus)
            .then(() => {
                this.setState({
                    store: {
                        ...this.state.store,
                        modifiers: this.state.store.modifiers.map(m => {
                            if (m.modifierId === modifier.modifierId) {
                                return {
                                    ...modifier,
                                    status: newStatus,
                                };
                            }
                            return m;
                        }),
                    },
                });
            })
            .catch(e => logger.warn(e))
            .finally(() => {
                this.setState({ loading: false })
            });
    };

    _onSaveProductUpdate = (status, statusReason) => {
        this.setState({ loading: true })
        stores
            .updateProduct(
                this.state.store.id,
                this.state.currentProduct.id,
                this.state.currentProductType,
                status,
                statusReason,
            )
            .then(() => {
                this.setState({
                    currentProduct: null,
                    store: {
                        ...this.state.store,
                        items:
                            this.state.currentProductType === productType.item
                                ? this.state.store.items.map(i => {
                                        if (i.id === this.state.currentProduct.id) {
                                            return {
                                                ...i,
                                                status,
                                                statusReason,
                                            };
                                        }
                                        return i;
                                    })
                                : this.state.store.items,
                        combos:
                            this.state.currentProductType === productType.combo
                                ? this.state.store.combos.map(c => {
                                        if (c.id === this.state.currentProduct.id) {
                                            return {
                                                ...c,
                                                status,
                                                statusReason,
                                            };
                                        }
                                        return c;
                                    })
                                : this.state.store.combos,
                    },
                });
            })
            .catch(err => logger.warn(err))
            .finally(() => {
                this.setState({ loading: false })
            });
    };

    _refreshTenders = () => {
        this.setState({ loading: true })
        stores
            .getStoreExternalTenders(this.state.store.id)
            .then((resp) => {
                this.setState({
                    store: {
                        ...this.state.store,
                        tenders: resp ? resp.tenders : null
                    },
                    loading: false
                })
            })
            .catch(err => {
                logger.warn(err);
                this._showAlert(strings.errorRefresh)
            })
            .finally(() => {
                this.setState({ loading: false })
            });
    }

    _onAddTender = () => {
        this.setState({
            currentTender: { name: "", accounts: []}
        })
    }

    _onEditTender = t => {
        this.setState({
            currentTender: t
        });
    }

    _onDeleteTender = t => {
        this.setState({
            selectedTender: t,
            showDeleteConfirmModal: true
        })
    }

    _onSaveTender = (tenderName, splitTender, accountName1, split1, accountName2, split2) => {
        var tenderNameTrim = tenderName.trim()
        var accountNameTrim1 = accountName1.trim()
        var accountNameTrim2 = accountName2.trim()
        var percentage1 = splitTender ? parseFloat(split1) : 100
        var percentage2 = splitTender ? parseFloat(split2): 0
        if (tenderNameTrim === "" || accountNameTrim1 === "" || (splitTender && accountNameTrim2 === "")) {
            this._showAlert(strings.nameCannotBeEmpty)
            return
        }
        if (tenderNameTrim !== this.state.currentTender.name && this.state.store.tenders.some(t => t.name === tenderNameTrim)) {
            this._showAlert(strings.namesMustBeUnique)
            return
        }
        if (isNaN(percentage1) || isNaN(percentage2)) {
            this._showAlert(strings.splitsMustBeValid)
            return
        }
        if (percentage1 < 0 || percentage2 < 0) {
            this._showAlert(strings.splitsMustBePositive)
            return
        }
        if (percentage1 + percentage2 !== 100.0) {
            this._showAlert(strings.splitsMustAddUp)
            return
        }
        var accounts = [{ name: accountNameTrim1, percentage: percentage1 }]
        if (splitTender) {
            accounts.push({ name: accountNameTrim2, percentage: percentage2 })
        }
        var tender = { name: tenderName, accounts}
        this.setState({ loading: true })
        if (this.state.currentTender.id != null) {
            if (tender.name === this.state.currentTender.name) {
                delete tender.name
            }
            if (tender.accounts.length === this.state.currentTender.accounts.length
                && tender.accounts.every(t1 => this.state.currentTender.accounts.some(t2 => t1.name === t2.name && t1.percentage === t2.percentage)
                )
            ) {
                delete tender.accounts
            }
            stores
                .editStoreExternalTender(this.state.store.id, this.state.currentTender.id, tender)
                .then(() => {
                    this._refreshTenders();
                    this.setState({
                        currentTender: null
                    })
                })
                .catch(err => {
                    logger.warn(err);
                    this._showAlert(strings.errorEdit)
                })
                .finally(() => {
                    this.setState({ loading: false })
                })
        } else {
            stores
                .addStoreExternalTender(this.state.store.id, tender)
                .then(() => {
                    this._refreshTenders();
                    this.setState({
                        currentTender: null
                    })
                })
                .catch(err => {
                    logger.warn(err);
                    this._showAlert(strings.errorAdd)
                })
                .finally(() => {
                    this.setState({ loading: false })
                })
        }
    }

    _closeDeleteConfirmModal = () => {
        this.setState({
            selectedTender: null,
            showDeleteConfirmModal: false
        })
    }

    _deleteTender = () => {
        this.setState({ loading: true })
        stores
            .deleteStoreExternalTender(this.state.store.id, this.state.selectedTender.id)
            .then(() => {
                this._refreshTenders();
                this.setState({
                    selectedTender: null,
                    showDeleteConfirmModal: false
                })
            })
            .catch((err) => {
                logger.warn(err)
                this._showAlert(strings.errorDelete)
            })
            .finally(() => {
                this.setState({ loading: false })
            })
    }

    _closeAlertModal = () => {
        this.setState({
            showAlertModal: false,
        })
    }

    _onReorderTender = (id, index) => {
        this.setState({ loading: true })
        stores
            .reorder(this.state.store.id, id, index)
            .then(() => {
                this._refreshTenders();
            })
            .catch((err) => {
                logger.warn(err)
                this._showAlert(strings.errorReorder)
            })
            .finally(() => {
                this.setState({ loading: false })
            })
    }

    render() {
        if (this.state.initialLoading) {
            return <Loader />;
        } else if (!this.state.store) {
            return <FailedToLoadAlert type={strings.store} />;
        }
        const store = this.state.store;
        return (
            <Grid>
                <Row bsClass="m-t">
                    <Col md={12}>
                        <h4>{store.name}</h4>
                    </Col>
                </Row>
                <Row bsClass="m-t">
                    <Col md={12}>
                        <div className={css(styles.tabWrapper)}>
                            <Tabs defaultActiveKey={1} id="uncontrolled-tab-example">
                                <Tab eventKey={1} title={strings.images}>
                                    <ImageSetUploader
                                        label={strings.heroImage}
                                        images={store ? store.images : null}
                                        onSubmit={this._handleUploadImage}
                                        minWidth={414}
                                        minHeight={350}
                                    />
                                </Tab>
                                <Tab eventKey={2} title={strings.featured}>
                                    <FeaturedProductTab
                                        onAdd={this._onAddFeaturedProduct}
                                        onRemove={this._onRemoveFeaturedProduct}
                                        onReorder={this._reorderFeaturedProduct}
                                        featuredProducts={store.featuredProducts}
                                    />
                                </Tab>
                                <Tab eventKey={3} title={strings.items}>
                                    <StoreProductTab
                                        products={store.items}
                                        onEdit={this._onEditItem}
                                        loading={this.state.loading}
                                    />
                                </Tab>
                                <Tab eventKey={4} title={strings.combos}>
                                    <StoreProductTab
                                        products={store.combos}
                                        onEdit={this._onEditCombo}
                                        loading={this.state.loading}
                                    />
                                </Tab>
                                <Tab eventKey={5} title={strings.modifiers}>
                                    <StoreModifierTab
                                        modifiers={store.modifiers}
                                        onToggleStatus={this._onToggleModifierStatus}
                                        loading={this.state.loading}
                                    />
                                </Tab>
                                <Tab eventKey={6} title={strings.posSettings}>
                                    <StorePosSettingsTab
                                        tenders={store.tenders}
                                        onAdd={this._onAddTender}
                                        onEdit={this._onEditTender}
                                        onDelete={this._onDeleteTender}
                                        onReorder={this._onReorderTender}
                                        loading={this.state.loading}
                                    />
                                </Tab>
                            </Tabs>
                        </div>
                    </Col>
                </Row>
                {this.state.currentProduct ? (
                    <StoreProductModal
                        key={this.state.currentProduct.id}
                        show
                        handleSave={this._onSaveProductUpdate}
                        handleClose={this._onCancelEdit}
                        status={this.state.currentProduct.status}
                        statusReason={this.state.currentProduct.statusReason}
                        title={`${this.state.store.name} - ${this.state.currentProduct.name}`}
                    />
                ) : null}
                {this.state.currentTender ? (
                    <ExternalTenderModal
                        key={this.state.currentTender.id}
                        show
                        tenderName={this.state.currentTender.name}
                        accounts={this.state.currentTender.accounts}
                        handleSave={this._onSaveTender}
                        handleClose={this._onCancelEdit}
                        title={`${this.state.store.name} - ${this.state.currentTender.name ? this.state.currentTender.name : "New External Tender"}`}
                        loading={this.state.loading}
                    />
                ) : null}
                <DeleteConfirmModal
                    show={this.state.showDeleteConfirmModal}
                    title={strings.confirmDelete}
                    handleClose={this._closeDeleteConfirmModal}
                    handleConfirm={this._deleteTender}
                />
                <AlertModal
                    show={this.state.showAlertModal}
                    alertMessage={this.state.alertMsg}
                    handleClose={this._closeAlertModal}
                />
                {this.state.loading ? (
                    <div className={css(styles.loadingContainer)}>
                        <Loader />
                    </div>
                ) : null}
            </Grid>
        );
    }
}
